import React, {
  useRef,
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ReactMapGL, {
  NavigationControl,
  Source,
  Layer,
  Popup,
  Marker,
  FullscreenControl,
} from 'react-map-gl';
import styles from './styles.scss';
import { useWindowResizer } from '../../../../src/web/utils/useWindowResizer';
import useCurrentPage from '../../../hooks/useCurrentPage';
import FacilityPopUp from './FacilityPopupContent';
import FacilityPin from './FacilityPin';
import MapPin from './MapPin';
import 'mapbox-gl/dist/mapbox-gl.css';

const { MAPBOX_API_TOKEN } = process.env;

export const MAX_ZOOM = 24;
export const MIN_ZOOM = 0;

const SOURCES_IDENTIFIER = 'facilities';
const CLUSTER_LAYER_IDENTIFIER = 'clusters';
const CLUSTER_COUNT_LAYER_IDENTIFIER = 'cluster-count';
const FACILITY_LAYER_IDENTIFIER = 'facility';

const INDIVIDUAL_PIN_RADIUS = 8;
const BOUNDS_PADDING = 50;
const EASING_DURATION = 500;

const clearUndefinedValues = actualObject => Object.fromEntries(
  // eslint-disable-next-line no-unused-vars
  Object.entries(actualObject).filter(([_, v]) => v !== undefined),
);

const CLUSTER_CONFIG = {
  'circle-radius': ['step', ['get', 'point_count'], 10, 2, 20, 5, 30, 10, 40],
  'circle-stroke-width': 0.5,
  'circle-stroke-color': '#fff',
};
const CLUSTER_TEXT_CONFIG = {
  'text-field': '{point_count_abbreviated}',
  'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
  'text-size': 14,
};

const FACILITY_CONFIG = {
  'circle-stroke-width': 1,
  'circle-stroke-color': '#fff',
  'circle-radius': INDIVIDUAL_PIN_RADIUS,
};

const propTypes = {
  facilities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  className: PropTypes.string,
  zoom: PropTypes.number,
  onZoom: PropTypes.func,
  center: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }),
  onCenterChange: PropTypes.func,
  pannable: PropTypes.bool,
  rotatable: PropTypes.bool,
  zoomable: PropTypes.bool,
  disableControls: PropTypes.bool,
  enableClustering: PropTypes.bool,
  clusterRadius: PropTypes.number,
  colors: PropTypes.shape({
    pin: PropTypes.string,
    cluster: PropTypes.string,
  }),
  mapStyle: PropTypes.string,
  allowPopup: PropTypes.bool,
};

const defaultProps = {
  zoom: undefined,
  onZoom: () => {},
  center: undefined,
  onCenterChange: () => {},
  className: undefined,
  pannable: true,
  rotatable: true,
  zoomable: true,
  disableControls: false,
  enableClustering: false,
  clusterRadius: 40,
  colors: {
    pin: 'primary',
    cluster: 'secondary',
  },
  mapStyle: 'mapbox://styles/mapbox/streets-v12',
  allowPopup: true,
};

const Map = forwardRef(
  (
    {
      className,
      pannable,
      rotatable,
      zoomable,
      disableControls,
      facilities = [],
      enableClustering,
      clusterRadius,
      zoom,
      center,
      onCenterChange,
      onZoom,
      colors,
      mapStyle,
      allowPopup,
      useDefaultZoomInAbsence,
    },
    ref,
  ) => {
    const map = useRef();
    const mapRef = useRef();

    const [loaded, setLoaded] = useState();
    const [focusedFacility, setFocusedFacility] = useState(null);
    let isSingleFacility;
    if (facilities.length === 1 && !zoom) {
      isSingleFacility = true;
    }
    const facilityFeatures = useMemo(
      () => ({
        type: 'FeatureCollection',
        features: facilities
          .filter(x => !x.isPrimary)
          .map(faci => ({
            type: 'Feature',
            properties: { id: faci.id },
            geometry: {
              type: 'Point',
              coordinates: [faci.longitude, faci.latitude],
            },
          })),
      }),
      [facilities],
    );

    const allFeaturesBound = useMemo(() => {
      if (facilityFeatures.features.length === 1) {
        return [
          [
            facilityFeatures.features[0].geometry.coordinates[0],
            facilityFeatures.features[0].geometry.coordinates[1],
          ],
          [
            facilityFeatures.features[0].geometry.coordinates[0],
            facilityFeatures.features[0].geometry.coordinates[1],
          ],
        ];
      }

      if (facilityFeatures.features.length > 1) {
        const allLatitudes = [
          ...new Set(
            facilityFeatures.features.map(f => f.geometry.coordinates[1]),
          ),
        ];
        const allLongitudes = [
          ...new Set(
            facilityFeatures.features.map(f => f.geometry.coordinates[0]),
          ),
        ];
        const north = Math.max(...allLatitudes);
        const south = Math.min(...allLatitudes);

        const east = Math.max(...allLongitudes);
        const west = Math.min(...allLongitudes);

        return [
          [west, south],
          [east, north],
        ];
      }
      return undefined;
    }, [facilityFeatures]);

    const facilityData = useMemo(
      () => facilities.reduce((acc, curr) => ({ ...acc, [curr.id]: curr }), {}),
      [facilities],
    );

    const currentFacility = facilities.find(x => x.isPrimary);

    const onMapLoaded = () => {
      let acquiredZoom = zoom || (useDefaultZoomInAbsence ? 13 : undefined);
      let acquiredCenter = center && center.latitude && center.longitude
        ? [center.longitude, center.latitude]
        : undefined;
      const acquiredBounds = allFeaturesBound;
      if (!acquiredCenter && facilities.length === 1) {
        acquiredCenter = [facilities[0].longitude, facilities[0].latitude];
        acquiredZoom = acquiredZoom || 13;
      }
      if (acquiredCenter) {
        mapRef.current.easeTo(
          clearUndefinedValues({
            center: acquiredCenter,
            zoom: acquiredZoom,
            duration: EASING_DURATION,
          }),
        );
      } else {
        mapRef.current.fitBounds(
          acquiredBounds,
          clearUndefinedValues({
            padding: BOUNDS_PADDING,
            zoom: acquiredZoom,
            duration: EASING_DURATION,
          }),
        );
      }
      setLoaded(true);
    };

    useEffect(() => {
      if (!mapRef.current) {
        return;
      }
      const currentZoom = mapRef.current.getZoom();
      if (zoom && currentZoom !== zoom) {
        mapRef.current.easeTo({
          zoom,
          duration: EASING_DURATION,
        });
      }
    }, [zoom]);

    useEffect(() => {
      if (!mapRef.current) {
        return;
      }
      const currentCenter = mapRef.current.getCenter();
      if (
        center
        && center.latitude
        && center.longitude
        && center.latitude !== currentCenter.lat
        && center.longitude !== currentCenter.lng
      ) {
        mapRef.current.easeTo({
          center: [center.longitude, center.latitude],
          duration: EASING_DURATION,
        });
      }
    }, [center]);

    const focusMarker = (facilityInfo) => {
      setFocusedFacility(facilityInfo);
      if (mapRef.current) {
        mapRef.current.flyTo({
          center: [facilityInfo.longitude, facilityInfo.latitude],
          duration: EASING_DURATION,
        });
      }
    };

    const onMapClick = (ev) => {
      const detectedFeature = ev.features?.[0];
      if (!detectedFeature) {
        return;
      }

      if (
        !disableControls
        && detectedFeature.layer.id === CLUSTER_LAYER_IDENTIFIER
      ) {
        mapRef.current
          .getSource(SOURCES_IDENTIFIER)
          .getClusterExpansionZoom(
            detectedFeature.properties.cluster_id,
            (err, obtainedZoom) => {
              if (!err) {
                mapRef.current.easeTo({
                  center: detectedFeature.geometry.coordinates,
                  zoom: obtainedZoom,
                  duration: EASING_DURATION,
                });
              }
            },
          );
      }

      if (
        detectedFeature.layer.id === FACILITY_LAYER_IDENTIFIER
        && allowPopup
      ) {
        focusMarker(facilityData[detectedFeature.properties.id]);
      }
    };

    useImperativeHandle(ref, () => ({
      zoomIn: () => {
        if (mapRef.current) {
          const currentZoom = mapRef.current.getZoom();
          mapRef.current.easeTo({
            zoom: currentZoom + 1,
            duration: EASING_DURATION,
          });
        }
      },
      zoomOut: () => {
        if (mapRef.current) {
          const currentZoom = mapRef.current.getZoom();
          mapRef.current.easeTo({
            zoom: currentZoom - 1,
            duration: EASING_DURATION,
          });
        }
      },
      getCurrentZoom: () => mapRef.current?.getZoom(),
    }));

    const classes = cx(styles.map, className, 'pb-no-click');
    const { height: contentHeight, width: contentWidth } = useWindowResizer({
      ref: map,
    });

    const currentPage = useCurrentPage();

    const getColor = (property) => {
      switch (colors[property]) {
        case 'primary':
        case 'secondary':
        case 'tertiary':
          return currentPage[`${colors[property]}Color`] || 'transparent';
        default:
          return colors[property];
      }
    };

    const onMarkerClick = facility => (markerClickEv) => {
      markerClickEv.stopPropagation();
      markerClickEv.preventDefault();
      focusMarker(facility);
    };

    return (
      <div className={classes} ref={map}>
        <ReactMapGL
          initialViewState={{
            bounds: (isSingleFacility || zoom || center) ? undefined : allFeaturesBound,
            width: contentWidth,
            height: contentHeight,
            fitBoundsOptions: { padding: BOUNDS_PADDING },
          }}
          height={contentHeight}
          width={contentWidth}
          ref={mapRef}
          projection="mercator" // This ensures its always going to be a flat map
          maxZoom={MAX_ZOOM}
          minZoom={MIN_ZOOM}
          doubleClickZoom={zoomable}
          scrollZoom={zoomable}
          touchZoom={zoomable}
          dragRotate={rotatable}
          touchRotate={rotatable}
          dragPan={pannable}
          interactiveLayerIds={
            enableClustering
              ? [CLUSTER_LAYER_IDENTIFIER, FACILITY_LAYER_IDENTIFIER]
              : undefined
          }
          onZoom={(ev) => {
            if (onZoom) {
              onZoom(ev.viewState.zoom);
            }
          }}
          onDragEnd={(ev) => {
            if (onCenterChange) {
              onCenterChange({
                latitude: ev.viewState.latitude,
                longitude: ev.viewState.longitude,
              });
            }
            if (onZoom) {
              onZoom(ev.viewState.zoom);
            }
          }}
          onClick={onMapClick}
          onLoad={onMapLoaded}
          mapboxAccessToken={MAPBOX_API_TOKEN}
          cooperativeGestures={!disableControls && zoomable}
          mapStyle={mapStyle}
        >
          <div className={styles.map}>
            {loaded && (
              <>
                {enableClustering ? (
                  <>
                    <Source
                      id={SOURCES_IDENTIFIER}
                      type="geojson"
                      data={facilityFeatures}
                      cluster={enableClustering}
                      // clusterRadius={40}
                      clusterRadius={clusterRadius}
                    >
                      <Layer
                        id={CLUSTER_LAYER_IDENTIFIER}
                        type="circle"
                        filter={['has', 'point_count']}
                        source={SOURCES_IDENTIFIER}
                        paint={{
                          ...CLUSTER_CONFIG,
                          'circle-color': getColor('cluster'),
                        }}
                      />
                      <Layer
                        id={CLUSTER_COUNT_LAYER_IDENTIFIER}
                        type="symbol"
                        filter={['has', 'point_count']}
                        source={SOURCES_IDENTIFIER}
                        layout={CLUSTER_TEXT_CONFIG}
                        paint={{
                          'text-color': '#fff',
                        }}
                      />
                      <Layer
                        id={FACILITY_LAYER_IDENTIFIER}
                        type="circle"
                        filter={['!', ['has', 'point_count']]}
                        source={SOURCES_IDENTIFIER}
                        paint={{
                          ...FACILITY_CONFIG,
                          'circle-color': getColor('pin'),
                        }}
                      />
                    </Source>
                  </>
                ) : (
                  <>
                    {facilityFeatures.features.map(facility => (
                      <Marker
                        data-testid="facilities-map-marker"
                        key={facility.properties.id}
                        active={focusedFacility?.id === facility.properties.id}
                        latitude={facility.geometry.coordinates[1]}
                        longitude={facility.geometry.coordinates[0]}
                      >
                        <MapPin
                          id={facility.properties.id}
                          active={
                            focusedFacility?.id === facility.properties.id
                          }
                          color={getColor('pin')}
                          onClick={onMarkerClick(
                            facilityData[facility.properties.id],
                          )}
                        />
                      </Marker>
                    ))}
                  </>
                )}
              </>
            )}

            {loaded && currentFacility && (
              <Marker
                latitude={currentFacility.latitude}
                longitude={currentFacility.longitude}
              >
                <FacilityPin
                  active={currentFacility.id === focusedFacility?.id}
                  onClick={onMarkerClick(currentFacility)}
                  id={currentFacility.id}
                />
              </Marker>
            )}

            {allowPopup && focusedFacility && (
              <Popup
                offset={[0, -INDIVIDUAL_PIN_RADIUS]}
                latitude={focusedFacility.latitude}
                longitude={focusedFacility.longitude}
                onClose={() => {
                  setFocusedFacility(null);
                }}
              >
                <FacilityPopUp facility={focusedFacility} />
              </Popup>
            )}

            {!disableControls && (
              <>
                <NavigationControl position="top-left" showCompass={false} />
                <FullscreenControl position="top-left" />
              </>
            )}
          </div>
        </ReactMapGL>
      </div>
    );
  },
);

Map.propTypes = propTypes;
Map.defaultProps = defaultProps;

export default Map;
